import i18n from "i18next";
import { initReactI18next } from "react-i18next";
import LanguageDetector from "i18next-browser-languagedetector";
import ChainedBackend from "i18next-chained-backend";
import resourcesToBackend from "i18next-resources-to-backend";
import { getGCPFile } from "./http/asset-management-services";
import translationLocalEN from "./locales/en/translation";
import rulesLocalEN from "./locales/en/rules";
import disclaimerLocalEN from "./locales/en/disclaimer";

import { lowerCase, merge } from "lodash";
import { getCountryCodeViaLocale } from "./util/functions";
import environment from "./environment";
import { AppConstants } from "./constants/AppConstants";

const currentLocale = navigator.language;
const selectedLanguage = localStorage.getItem("i18nextLng");
getCountryCodeViaLocale(currentLocale);

const loadCurrentLocaleTranslation = `${AppConstants.BASE_STORAGE_URL}/${
  AppConstants.BASE_STORAGE_URL_TRANSLATION_SUFFIX
}/translation_${selectedLanguage || currentLocale.trim()}.json`;

const resources: any = {
  en: {
    [AppConstants.LOCALISATION_NAMESPACE_TRANSLATION]: translationLocalEN,
    [AppConstants.LOCALISATION_NAMESPACE_RULES]: rulesLocalEN,
    [AppConstants.LOCALISATION_NAMESPACE_DISCLAIMER]: disclaimerLocalEN,
  },
};

const cache: any = {};

export const getCurrentLanguageFile = async (
  language: string,
  namespace: string
) => {
  const languageName = language.split("-")[0];
  const cacheKeyFull = `${namespace}_${language}`;
  const cacheKeyBase = `${namespace}_${languageName}`;

  if (cache[cacheKeyFull]) {
    return cache[cacheKeyFull];
  }

  if (cache[cacheKeyBase]) {
    return cache[cacheKeyBase];
  }


  let url: string =
    namespace === "rules"
      ? `${AppConstants.BASE_STORAGE_URL}/${AppConstants.getRulesSuffix()}/${namespace}_${language}.json`
      : namespace === "disclaimer"
      ? `${AppConstants.BASE_STORAGE_URL}/${AppConstants.getDisclaimerSuffix()}/${namespace}_${language}.json`
      : namespace === "adverse"
      ? `${AppConstants.BASE_STORAGE_URL}/${AppConstants.getAdverseSuffix()}/${namespace}_${language}.json`
      : `${AppConstants.BASE_STORAGE_URL}/${AppConstants.getTranslationSuffix()}/${namespace}_${language}.json`;

  try {
    const latestLanguageFile = await getGCPFile(url);
    const { data } = latestLanguageFile;
    getCountryCodeViaLocale(language);
    if (namespace === AppConstants.LOCALISATION_NAMESPACE_ADVERSE) {
      let arr = Object.keys(data);
      if (arr.length) {
        AppConstants.SUPPORTED_ADVERSE_COUNTRIES = arr;
      }
      sessionStorage.setItem(
        AppConstants.LOCALISATION_NAMESPACE_ADVERSE,
        "true"
      );
    }
 
    const mergedData = merge({}, resources.en[namespace], data);
    cache[cacheKeyFull] = mergedData;
    return mergedData;
  } catch (error) {
    try {
      getCountryCodeViaLocale(language);
      if (namespace === AppConstants.LOCALISATION_NAMESPACE_ADVERSE) {
        sessionStorage.setItem(
          AppConstants.LOCALISATION_NAMESPACE_ADVERSE,
          "false"
        );
      }
      const fallbackLanguageFile = await getGCPFile(url);
      const { data } = fallbackLanguageFile;
      const mergedData = merge({}, resources.en[namespace], data);
      cache[cacheKeyFull] = mergedData;
      return mergedData;
    } catch (fallbackError) {
      return resources.en[namespace];
    }
  }
};

const initialLanguage = selectedLanguage || currentLocale;

i18n
  .use(LanguageDetector)
  .use(initReactI18next)
  .use(ChainedBackend)
  .init({
    lng: initialLanguage,
    fallbackLng: "en",
    ns: [
      AppConstants.LOCALISATION_NAMESPACE_TRANSLATION,
      AppConstants.LOCALISATION_NAMESPACE_RULES,
      AppConstants.LOCALISATION_NAMESPACE_DISCLAIMER,
      AppConstants.LOCALISATION_NAMESPACE_ADVERSE,
    ],
    defaultNS: "translation",
    // debug: true,
    backend: {
      backends: [
        resourcesToBackend((language, namespace, callback) => {
          getCurrentLanguageFile(language, namespace)
            .then((resources) => {
              callback(null, resources);
            })
            .catch((error) => {
              callback(error, null);
            });
        }),
      ],
      backendOptions: [
        {
          loadPath: loadCurrentLocaleTranslation,
        },
      ],
    },
    detection: {
      order: ["localStorage", "navigator"],
      lookupLocalStorage: AppConstants.LOCAL_STORAGE_LOCALE_KEY,
    },
    interpolation: {
      escapeValue: false,
    },
  });

export default i18n;
